import { useState } from 'react';
import Link from 'next/link';

import dayjs from 'dayjs';

import useAcl from '@/hooks/user/useAcl';
import { CLEAR_GROUP_INFO, useGlobalInfo } from '@/utils/hooks/context/useGlobalInfo';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { post } from '@/utils/client';
import { useRouter } from '@/hooks/utils/useNewRouter';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { CUSTOM, PREVIOUS_MONTH } from '@/lib/periods';
import { FormatRadioValues, REPORT_TYPE_DASHBOARDS } from '@/lib/reports/reportOptions';
import { CREATE_REPORT } from '@/lib/routes/apiEndpoints';
import { DASHBOARD_CUSTOMIZATION, REPORTS_DASHBOARDS } from '@/lib/routes/privateRoutes';

import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import { RadioGroup } from '@/components/composite/Form/RadioGroup';
import ExportXlsModal from '@/components/composite/Modals/ExportXlsModal';
import { Trans } from '@/components/tools/translation';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';

const exportTypes = FormatRadioValues()[REPORT_TYPE_DASHBOARDS];

const ExportModal = ({ isOpen, toggle, dashboard, isLocked }) => {
    const { t } = useTranslation('commons');
    const [selectedExportType, setExportType] = useState(exportTypes[0].value);
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { hasAcl } = useAcl();
    const router = useRouter();
    const { activeSocialProfile, setActiveSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { dispatch } = useGlobalInfo();

    const createReport = async (email) => {
        const params = {
            report_type: selectedExportType,
            report_title: dashboard?.name,
            send_email: true,
            emails: [email],
            is_scheduled: false,
            period: dashboard?.data?.period?.period || PREVIOUS_MONTH,
            dashboards: [dashboard.id],
            timezone: user?.settings?.timezone || 'Europe/Paris',
            comparison_period: dashboard?.data?.period?.compareTo
        };
        if (dashboard?.data?.period?.period === CUSTOM) {
            params.start_date = dayjs(dashboard?.data?.period?.start).format('YYYY-MM-DD HH:mm:ss');
            params.end_date = dayjs(dashboard?.data?.period?.end).format('YYYY-MM-DD HH:mm:ss');
        }
        await post(CREATE_REPORT, params);
    };

    return (
        <ExportXlsModal
            isLocked={isLocked}
            isOpen={isOpen}
            toggle={toggle}
            confirmButtonId="export-dashboard-confirm-button"
            sendReport={createReport}
            title={
                <Trans
                    i18nKey="dashboard:export_xls.title"
                    components={{ span: <span className="text-icoBlue" /> }}
                    values={{
                        dashboard: dashboard?.name
                    }}
                />
            }
            headerExtraContent={
                hasAcl(aclFeatures.report.white_label.read) &&
                !dashboard?.data?.report_customization ? (
                    <p className="mb-4 mt-8 rounded-m border border-icoBlue-200 bg-icoBlue-100 px-5 py-4 text-center text-xs text-icoBlue dark:border-icoBlue dark:bg-icoBlue/20">
                        <Trans
                            i18nKey="dashboard:export_xls.white_label_your_dashboard"
                            components={{
                                link: (
                                    <Link
                                        href={DASHBOARD_CUSTOMIZATION.replace('[id]', dashboard.id)}
                                        className="underline"
                                    />
                                )
                            }}
                        />
                    </p>
                ) : null
            }
            footerExtraButton={
                <Button
                    type="button"
                    onClick={() => {
                        if (!activeSocialProfile) {
                            setActiveSocialProfile(activatedSocialProfiles[0]);
                            dispatch({ type: CLEAR_GROUP_INFO });
                        }
                        router.push(`${REPORTS_DASHBOARDS}?dashboards=${dashboard.id}`);
                    }}
                    variant="secondary"
                    disabled={isLocked}>
                    {t('dashboard:export_xls.schedule_recurring_report')}
                    {isLocked && (
                        <TooltipPlanRestriction position="top">
                            <Icon icon="crown" className="ml-2 text-icoBlue" />
                        </TooltipPlanRestriction>
                    )}
                </Button>
            }>
            <RadioGroup
                className="mt-6"
                values={exportTypes}
                current={selectedExportType}
                label={t('reports.create.label_format')}
                onChange={(e) => setExportType(e.target.value)}
                isLocked={isLocked}
            />
        </ExportXlsModal>
    );
};

export default ExportModal;
